import React, { useEffect, useState, useMemo } from "react";
import {
  PDFViewer,
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import logoImage from "../../assets/logo-prima-hitam.png";
import moment from "moment";
import "moment/locale/id";

moment.locale("id");

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontSize: 9,
    fontFamily: "Helvetica",
    backgroundColor: "#ffffff",
  },

  /* ================= HEADER ================= */
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 15,
    paddingBottom: 10,
    borderBottom: "2px solid #333",
  },

  headerLeft: {
    width: "60%",
  },

  headerRight: {
    width: "40%",
    alignItems: "flex-end",
  },

  logo: {
    width: 80,
    marginBottom: 5,
  },

  documentTitle: {
    fontSize: 10,
    fontWeight: "bold",
    marginBottom: 4,
    letterSpacing: 1,
  },

  documentSubtitle: {
    fontSize: 10,
    color: "#666",
  },

  /* ================= TABLE ================= */
  table: {
    width: "100%",
    marginTop: 10,
  },

  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f0f0f0",
    borderBottomWidth: 1,
    borderBottomColor: "#333",
    paddingVertical: 6,
    paddingHorizontal: 4,
  },

  tableHeaderCell: {
    fontSize: 8,
    fontWeight: "bold",
    textAlign: "center",
    color: "#333",
  },

  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: "#ccc",
    paddingVertical: 5,
    paddingHorizontal: 4,
    minHeight: 25,
    alignItems: "center",
  },

  tableRowEven: {
    backgroundColor: "#fafafa",
  },

  /* Column widths */
  colNo: { width: "5%" },
  colKeterangan: { width: "30%" },
  colTipe: { width: "15%" },
  colSupplier: { width: "15%" },
  colNominal: { width: "15%" },
  colSetujui: { width: "10%" },
  colPindahTanggal: { width: "10%" },

  tableCell: {
    fontSize: 8,
    textAlign: "center",
  },

  tableCellLeft: {
    fontSize: 8,
    textAlign: "left",
  },

  tableCellRight: {
    fontSize: 8,
    textAlign: "right",
  },

  /* ================= GRAND TOTAL ================= */
  grandTotalRow: {
    flexDirection: "row",
    backgroundColor: "#333",
    paddingVertical: 8,
    paddingHorizontal: 4,
    marginTop: 5,
  },

  grandTotalLabel: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#fff",
    textAlign: "left",
  },

  grandTotalValue: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#fff",
    textAlign: "right",
  },

  /* ================= FOOTER ================= */
  footer: {
    marginTop: 20,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: "#ddd",
  },

  footerText: {
    fontSize: 8,
    color: "#666",
    textAlign: "right",
  },

  signBox: {
    marginTop: 30,
    flexDirection: "row",
    justifyContent: "flex-end",
  },

  signArea: {
    width: 150,
    textAlign: "center",
  },

  signLine: {
    marginTop: 50,
    borderBottomWidth: 1,
    borderBottomColor: "#333",
    marginBottom: 5,
  },
});

const CetakAlokasiDocument = ({ data, tanggal, tipe }) => {
  const formatRupiah = (value) => {
    if (!value || value === "0" || value === "0.00") return "-";
    const num = parseFloat(value);
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(num);
  };

  const totalNominal = data.reduce(
    (sum, item) => sum + (Number(item.nominal) || 0),
    0,
  );

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Image
              src={logoImage}
              alt="PRIMA Centre Logo"
              style={styles.logo}
            />
            {/* <Text style={{ fontSize: 9, color: "#666", marginTop: 2 }}>
              Jl. RE. Martadinata No.48, Cipaning, Kec. Ciborong,{"\n"}
              Kota Sukabumi, Jawa Barat 43122
            </Text> */}
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.documentTitle}>
              LEMBAR PERSETUJUAN ALOKASI PENGELUARAN
            </Text>
            <Text style={styles.documentSubtitle}>
              Tanggal: {moment(tanggal).format("DD MMMM YYYY")}
            </Text>
          </View>
        </View>

        {/* Table */}
        <View style={styles.table}>
          {/* Table Header */}
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderCell, styles.colNo]}>No</Text>
            <Text style={[styles.tableHeaderCell, styles.colKeterangan]}>
              Keterangan
            </Text>
            <Text style={[styles.tableHeaderCell, styles.colTipe]}>
              Kode Transaksi
            </Text>
            <Text style={[styles.tableHeaderCell, styles.colSupplier]}>
              Supplier
            </Text>
            <Text
              style={[
                styles.tableHeaderCell,
                styles.colNominal,
                { textAlign: "right" },
              ]}
            >
              Nominal
            </Text>
            <Text style={[styles.tableHeaderCell, styles.colSetujui]}>
              Setujui (✓)
            </Text>
            <Text style={[styles.tableHeaderCell, styles.colPindahTanggal]}>
              Pindah Tanggal
            </Text>
          </View>

          {/* Table Body */}
          {data.map((row, index) => (
            <View
              key={index}
              style={[styles.tableRow, index % 2 === 1 && styles.tableRowEven]}
            >
              <Text style={[styles.tableCell, styles.colNo]}>{index + 1}</Text>
              <Text style={[styles.tableCellLeft, styles.colKeterangan]}>
                {row.nama || "-"}
              </Text>
              <Text style={[styles.tableCellLeft, styles.colTipe]}>
                {row.nama_perkiraan || "-"}
              </Text>
              <Text style={[styles.tableCellLeft, styles.colSupplier]}>
                {row.nama_supplier || "-"}
              </Text>
              <Text style={[styles.tableCellRight, styles.colNominal]}>
                {formatRupiah(row.nominal)}
              </Text>
              <Text style={[styles.tableCell, styles.colSetujui]}></Text>
              <Text style={[styles.tableCell, styles.colPindahTanggal]}></Text>
            </View>
          ))}
        </View>

        {/* Grand Total */}
        <View style={styles.grandTotalRow}>
          <Text style={[styles.grandTotalLabel, { width: "65%" }]}>
            TOTAL {tipe === "pemasukan" ? "PEMASUKAN" : "PENGELUARAN"}
          </Text>
          <View
            style={{
              width: "35%",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            <Text
              style={[
                styles.grandTotalValue,
                { width: "42%", textAlign: "right" },
              ]}
            >
              {formatRupiah(totalNominal)}
            </Text>
            <Text style={{ width: "28%" }}></Text>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            Dokumen ini dicetak dari Website PRIMA Centre pada{" "}
            {moment().format("DD MMMM YYYY, HH:mm")}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

const CetakAlokasi = () => {
  const [data, setData] = useState([]);
  const [tanggal, setTanggal] = useState("");
  const [tipe, setTipe] = useState("");

  useEffect(() => {
    const data_source = sessionStorage.getItem("datacetakalokasi");
    if (data_source) {
      const parsed = JSON.parse(data_source);
      setData(parsed.data || []);
      setTanggal(parsed.tanggal || moment().format());
      setTipe(parsed.tipe || "pengeluaran");
    }
  }, []);

  return (
    <div style={{ height: "100vh" }}>
      <PDFViewer width="100%" height="100%">
        <CetakAlokasiDocument data={data} tanggal={tanggal} tipe={tipe} />
      </PDFViewer>
    </div>
  );
};

export default CetakAlokasi;
